import React, { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { getAllInvoices, updateInvoiceAndPayment } from "../../services/Invoices/invoice.service";
import { toast } from "react-toastify";
import "./InvoiceList.css";

interface Invoice {
  id: number;
  orderId: string;
  BuyerName: string;
  matchSellerName: string;
  buyertotalAmount: string;
  buyertotalAmmoumtGst: string;
  sellertotalAmount: string;
  gstAmount?: string; // ✅ Added
  createdAt: string;
  orderStatus: string;
  paymentStatus?: string;
  invoicepdf?: string;
  category?: {
    id: number;
    name: string;
  };
  metalType?: {
    id: number;
    name: string;
  };
  marginWastageProfit?: number;
  marginMakingChargesProfit?: number;
  totalAdminProfit?: number;
  discountAmount?: number,
  discountAfterAmount?: number,
  discountAfterAmountWithGst?: number,
  item?: {
    articleName?: string;
    image?: string[];
  } | null;
}

const InvoiceList = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const itemsPerPage = 10;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [paymentStatus, setPaymentStatus] = useState("Pending");
  const [loading, setLoading] = useState(false);
  const [buyerFilter, setBuyerFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [articleFilter, setArticleFilter] = useState("");
  const [metalFilter, setMetalFilter] = useState("");

  const uniqueBuyers = [...new Set(invoices.map(inv => inv.BuyerName).filter(Boolean))];
  const uniqueSellers = [...new Set(invoices.map(inv => inv.matchSellerName).filter(Boolean))];
  const uniqueCategories = [...new Set(invoices.map(inv => inv.category?.name).filter(Boolean))];
  const uniqueArticles = [...new Set(invoices.map(inv => inv.item?.articleName).filter(Boolean))];
  const uniqueMetals = [...new Set(invoices.map(inv => inv.metalType?.name).filter(Boolean))];

  const fetchInvoices = async () => {
    try {
      const res = await getAllInvoices();
      const data = res?.data?.data || [];
      setInvoices(data);
      setFilteredInvoices(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch invoices");
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  useEffect(() => {
    let data = [...invoices];

    if (searchText) {
      data = data.filter((inv) =>
        `${inv.orderId} ${inv.BuyerName} ${inv.matchSellerName} ${inv.category?.name || ''} ${inv.item?.articleName || ''} ${inv.metalType?.name || ''}`
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }

    if (buyerFilter) data = data.filter(inv => inv.BuyerName === buyerFilter);
    if (sellerFilter) data = data.filter(inv => inv.matchSellerName === sellerFilter);
    if (categoryFilter) data = data.filter(inv => inv.category?.name === categoryFilter);
    if (articleFilter) data = data.filter(inv => inv.item?.articleName === articleFilter);
    if (metalFilter) data = data.filter(inv => inv.metalType?.name === metalFilter);

    setFilteredInvoices(data);
    setCurrentPage(1);
  }, [searchText, invoices, buyerFilter, sellerFilter, categoryFilter, articleFilter, metalFilter]);

  const resetFilters = () => {
    setSearchText("");
    setBuyerFilter("");
    setSellerFilter("");
    setCategoryFilter("");
    setArticleFilter("");
    setMetalFilter("");
    setCurrentPage(1);
    toast.info("All filters cleared");
  };

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedData = filteredInvoices
    .slice(startIndex, startIndex + itemsPerPage)
    .map((item, index) => ({
      ...item,
      serial: startIndex + index + 1,
    }));

  const handleUpdateInvoice = async () => {
    if (!selectedInvoice) return;

    try {
      setLoading(true);
      const formData = new FormData();
      if (selectedFile) formData.append("invoicepdf", selectedFile);
      formData.append("paymentStatus", paymentStatus);
      await updateInvoiceAndPayment(selectedInvoice.orderId, formData);
      toast.success("Invoice updated successfully");
      setSelectedInvoice(null);
      setSelectedFile(null);
      fetchInvoices();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update invoice");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  return (
    <div className="employee-container">
      {/* HEADER */}
      <div className="header-bar">
        <div className="filters-row" style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "15px" }}>

          <input
            type="text"
            placeholder="Search by Order ID, Buyer, Seller..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: "250px",
              height: "38px",
              padding: "0 10px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />

          {uniqueBuyers.length > 0 && (
            <select
              value={buyerFilter}
              onChange={(e) => setBuyerFilter(e.target.value)}
              style={{
                width: "180px",
                height: "38px",
                padding: "0 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">All Buyers</option>
              {uniqueBuyers.map((buyer, idx) => (
                <option key={idx} value={buyer}>{buyer}</option>
              ))}
            </select>
          )}

          {uniqueSellers.length > 0 && (
            <select
              value={sellerFilter}
              onChange={(e) => setSellerFilter(e.target.value)}
              style={{
                width: "180px",
                height: "38px",
                padding: "0 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">All Sellers</option>
              {uniqueSellers.map((seller, idx) => (
                <option key={idx} value={seller}>{seller}</option>
              ))}
            </select>
          )}

          {uniqueArticles.length > 0 && (
            <select
              value={articleFilter}
              onChange={(e) => setArticleFilter(e.target.value)}
              style={{
                width: "180px",
                height: "38px",
                padding: "0 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">All Articles</option>
              {uniqueArticles.map((article, idx) => (
                <option key={idx} value={article}>{article}</option>
              ))}
            </select>
          )}

          {uniqueCategories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{
                width: "180px",
                height: "38px",
                padding: "0 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map((category, idx) => (
                <option key={idx} value={category}>{category}</option>
              ))}
            </select>
          )}

          {uniqueMetals.length > 0 && (
            <select
              value={metalFilter}
              onChange={(e) => setMetalFilter(e.target.value)}
              style={{
                width: "180px",
                height: "38px",
                padding: "0 10px",
                borderRadius: "6px",
                border: "1px solid #ccc",
              }}
            >
              <option value="">All Metals</option>
              {uniqueMetals.map((metal, idx) => (
                <option key={idx} value={metal}>{metal}</option>
              ))}
            </select>
          )}

          {(searchText || buyerFilter || sellerFilter || categoryFilter || articleFilter || metalFilter) && (
            <button
              onClick={resetFilters}
              style={{
                padding: "0 15px",
                height: "38px",
                borderRadius: "6px",
                border: "1px solid #dc3545",
                backgroundColor: "#dc3545",
                color: "white",
                cursor: "pointer",
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666", marginTop: "10px" }}>
        Showing {paginatedData.length} of {filteredInvoices.length} invoices (Total: {invoices.length})
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="employee-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
              <th style={{ padding: "12px", textAlign: "left" }}>S.NO</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Order ID</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Buyer</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Seller</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Article</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Category</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Metal Type</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Buyer Amount</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Buyer Amount + GST</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Seller Amount</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Discount Amount</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Discount Total Amount</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Discount Amount+ GST</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Margin Wastage Profit</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Making Charge Profit</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Admin Profit</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Payment Status</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Date</th>
              <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((inv) => {
              // ✅ Calculate seller amount with GST
              const sellerAmountWithGST = parseFloat(inv.sellertotalAmount || '0') + parseFloat(inv.gstAmount || '0');
              
              return (
                <tr key={inv.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "10px" }}>{inv.serial}</td>
                  <td style={{ padding: "10px" }}>{inv.orderId}</td>
                  <td style={{ padding: "10px" }}>{inv.BuyerName || "N/A"}</td>
                  <td style={{ padding: "10px" }}>{inv.matchSellerName || "N/A"}</td>
                  <td style={{ padding: "10px" }}>{inv.item?.articleName || "N/A"}</td>
                  <td style={{ padding: "10px" }}>{inv.category?.name || "-"}</td>
                  <td style={{ padding: "10px" }}>{inv.metalType?.name || "-"}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.buyertotalAmount || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.buyertotalAmmoumtGst || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {sellerAmountWithGST.toFixed(2)}</td> {/* ✅ Fixed */}
                  <td style={{ padding: "10px" }}>₹ {Number(inv.discountAmount || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.discountAfterAmount || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.discountAfterAmountWithGst || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.marginWastageProfit || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.marginMakingChargesProfit || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>₹ {Number(inv.totalAdminProfit || 0).toFixed(2)}</td>
                  <td style={{ padding: "10px" }}>
                    <span
                      style={{
                        color: inv.orderStatus === "Delivered" ? "green" :
                          inv.orderStatus === "On The Way" ? "orange" :
                            inv.orderStatus === "In Progress" ? "blue" : "gray",
                        fontWeight: "500",
                      }}
                    >
                      {inv.orderStatus || "Pending"}
                    </span>
                  </td>
                  <td style={{ padding: "10px" }}>
                    <span
                      style={{
                        color:
                          inv.paymentStatus === "Paid"
                            ? "green"
                            : inv.paymentStatus === "Pending"
                              ? "orange"
                              : "red",
                        fontWeight: "500",
                      }}
                    >
                      {inv.paymentStatus || "Pending"}
                    </span>
                  </td>
                  <td style={{ padding: "10px" }}>{formatDate(inv.createdAt)}</td>
                  <td style={{ padding: "10px", textAlign: "center" }}>
                    <button
                      onClick={() => {
                        setSelectedInvoice(inv);
                        setPaymentStatus(inv.paymentStatus || "Pending");
                        setSelectedFile(null);
                      }}
                      style={{
                        background: "#2196F3",
                        border: "none",
                        cursor: "pointer",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        color: "white",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      <Eye size={16} /> View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination" style={{ marginTop: "20px", display: "flex", gap: "10px", justifyContent: "center", alignItems: "center" }}>
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: "8px 16px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              backgroundColor: currentPage === 1 ? "#ccc" : "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
            }}
          >
            Previous
          </button>
          <span style={{ padding: "8px 16px" }}>Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 16px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              backgroundColor: currentPage === totalPages ? "#ccc" : "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* MODAL - SAME THEME, ONLY TABLE FORMAT FOR DATA */}
      {selectedInvoice && (
        <div className="modal-overlay" style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000
        }}>
          <div className="modal-box" style={{
            width: "800px",
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "#0f172a",
            borderRadius: "8px",
            padding: "20px"
          }}>
            <h2 style={{ marginBottom: "20px" }}>Invoice Details - #{selectedInvoice.orderId}</h2>

            {/* DATA IN TABLE FORMAT */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px", border: "1px solid #ddd" }}>
              <tbody>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px", fontWeight: "bold", width: "200px", backgroundColor: "#0f172a" }}>Order ID</td>
                  <td style={{ padding: "10px" }}>{selectedInvoice.orderId}</td>
                  <td style={{ padding: "10px", fontWeight: "bold", width: "200px", backgroundColor: "#0f172a" }}>Order Status</td>
                  <td style={{ padding: "10px" }}>
                    <span style={{ color: selectedInvoice.orderStatus === "Delivered" ? "green" : selectedInvoice.orderStatus === "On The Way" ? "orange" : selectedInvoice.orderStatus === "In Progress" ? "blue" : "gray" }}>
                      {selectedInvoice.orderStatus || "Pending"}
                    </span>
                  </td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px", fontWeight: "bold", backgroundColor: "#0f172a" }}>Buyer Name</td>
                  <td style={{ padding: "10px" }}>{selectedInvoice.BuyerName || "N/A"}</td>
                  <td style={{ padding: "10px", fontWeight: "bold", backgroundColor: "#0f172a" }}>Seller Name</td>
                  <td style={{ padding: "10px" }}>{selectedInvoice.matchSellerName || "N/A"}</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px", fontWeight: "bold", backgroundColor: "#0f172a" }}>Article</td>
                  <td style={{ padding: "10px" }}>{selectedInvoice.item?.articleName || "N/A"}</td>
                  <td style={{ padding: "10px", fontWeight: "bold", backgroundColor: "#0f172a" }}>Category</td>
                  <td style={{ padding: "10px" }}>{selectedInvoice.category?.name || "-"}</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px", fontWeight: "bold", backgroundColor: "#0f172a" }}>Metal Type</td>
                  <td style={{ padding: "10px" }}>{selectedInvoice.metalType?.name || "-"}</td>
                  <td style={{ padding: "10px", fontWeight: "bold", backgroundColor: "#0f172a" }}>Date</td>
                  <td style={{ padding: "10px" }}>{formatDate(selectedInvoice.createdAt)}</td>
                </tr>
              </tbody>
            </table>

            {/* FINANCIAL DETAILS TABLE */}
            <h3 style={{ marginBottom: "10px" }}>Financial Details</h3>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px", border: "1px solid #ddd" }}>
              <thead>
                <tr style={{ backgroundColor: "#0f172a", borderBottom: "1px solid #ddd" }}>
                  <th style={{ padding: "10px", textAlign: "left" }}>Particulars</th>
                  <th style={{ padding: "10px", textAlign: "right" }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px" }}>Buyer Amount</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>₹ {Number(selectedInvoice.buyertotalAmount || 0).toFixed(2)}</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px" }}>Buyer Amount + GST</td>
                  <td style={{ padding: "10px", textAlign: "right", fontWeight: "bold" }}>₹ {Number(selectedInvoice.buyertotalAmmoumtGst || 0).toFixed(2)}</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px" }}>Seller Amount</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>
                    {/* ✅ Fixed */}
                    ₹ {Number((parseFloat(selectedInvoice.sellertotalAmount || '0') + parseFloat(selectedInvoice.gstAmount || '0'))).toFixed(2)}
                  </td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px" }}>Admin Margin Wastage Profit</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>₹ {Number(selectedInvoice.marginWastageProfit || 0).toFixed(2)}</td>
                </tr>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "10px" }}>Admin Margin Making Charges Profit</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>₹ {Number(selectedInvoice.marginMakingChargesProfit || 0).toFixed(2)}</td>
                </tr>
                <tr style={{ backgroundColor: "#0f172a" }}>
                  <td style={{ padding: "10px", fontWeight: "bold" }}>Total Admin Profit</td>
                  <td style={{ padding: "10px", textAlign: "right", fontWeight: "bold", fontSize: "16px" }}>₹ {Number(selectedInvoice.totalAdminProfit || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            {/* IMAGE */}
            <div style={{ marginBottom: "20px" }}>
              <strong>Product Image:</strong>
              <div style={{ marginTop: "10px" }}>
                {selectedInvoice.item?.image?.length ? (
                  <img
                    src={`https://api.sonachandiwale.com${selectedInvoice.item.image[0]}`}
                    alt="product"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <p style={{ color: "#666" }}>No Image Available</p>
                )}
              </div>
            </div>

            {/* CURRENT INVOICE PDF */}
            {selectedInvoice.invoicepdf && (
              <div className="form-group pdf-box" style={{ marginBottom: "20px" }}>
                <label className="pdf-label" style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>
                  📄 Current Invoice
                </label>
                <a
                  href={selectedInvoice.invoicepdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    padding: "8px 16px",
                    backgroundColor: "#4CAF50",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                  }}
                >
                  👁 View PDF
                </a>
              </div>
            )}

            {/* PAYMENT STATUS */}
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Payment Status</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
                <option value="Failed">Failed</option>
                <option value="Refunded">Refunded</option>
              </select>
            </div>

            {/* FILE UPLOAD */}
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>Upload Invoice PDF</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && file.type !== "application/pdf") {
                    toast.warning("Only PDF files are allowed");
                    return;
                  }
                  setSelectedFile(file || null);
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              />
            </div>

            {/* ACTIONS */}
            <div className="modal-actions" style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}>
              <button
                onClick={() => setSelectedInvoice(null)}
                style={{
                  background: "#777",
                  color: "#fff",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
              <button
                onClick={handleUpdateInvoice}
                disabled={loading}
                style={{
                  background: loading ? "#ccc" : "#2196F3",
                  color: "#fff",
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;